'use client';
import type { EnterMode, PositionType, RenderNodesMap } from '@/types/commontypes';
import { ShapeType } from '@/types/commontypes';
import React, { forwardRef, useState } from 'react';
import { useStore } from 'zustand';
import { ORDER_LINE_RENDER_DEFAULTS } from '@/utils/shapeConverter';
import type { MenuProps } from 'antd';
import { Button, Dropdown, InputNumber, notification, theme } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import { useChartShapes, useChartUIContext } from '@/contexts';
import { useChartUIStoreSelector } from '@/stores/chartUIStore';
import { useGlobalShapeSelector } from '../../contexts/GlobalShapesContext';
import { globalShapeStore } from '../../stores/globalShapeStore';
import { COLORS } from '@/constants/colors';
import { buildTpslMenu } from './RatioDropDown';

/** 팝업 최초 오픈 시 Amount 입력란에 채워지는 기본값 (USDT) */
const DEFAULT_AMOUNT = 400;

/** 주문 가격 방식 — instant: 현재가 즉시 체결, limit: 지정가 (미구현) */
type PriceMode = 'instant' | 'limit';

/** position에 따른 버튼 색상·라벨을 반환 — 변경 시 이 함수만 수정하면 모든 곳에 반영 */
const getPositionStyle = (pos: EnterMode) => ({
  color: pos === 'hedge' ? COLORS.hedge : pos === 'short' ? COLORS.short : COLORS.long,
  label: pos === 'hedge' ? 'Enter Hedge' : pos === 'short' ? 'Enter Short' : 'Enter Long',
});

// ─── 메인 컴포넌트 ───────────────────────────────────────────────────
export const PlaceOrderPopup = forwardRef<HTMLDivElement>((_, ref) => {
  const { chartUIStore } = useChartUIContext();
  const yCoordinateValue = useChartUIStoreSelector(chartUIStore, s => s.yCoordinateValue);
  const activePopup = useChartUIStoreSelector(chartUIStore, s => s.activePopup);
  const setActivePopup = useChartUIStoreSelector(chartUIStore, s => s.setActivePopup);
  const setShapeTypeToDraw = useGlobalShapeSelector(s => s.setShapeTypeToDraw);

  const { shapeStore } = useChartShapes();
  const setRenderNodes = useStore(shapeStore, s => s.setRenderNodes);

  // hedgeMode 상태 구독 — hedge 진입 가능 여부 판단에 사용
  const hedgeMode = useGlobalShapeSelector(s => s.hedgeMode);

  const { token } = theme.useToken();

  const [inputAmount, setInputAmount] = useState<number | null>(DEFAULT_AMOUNT);

  // 팝업 오픈 시 방향별 마지막 TP/SL 비율을 globalShapeStore에서 복원
  const initialPos = activePopup?.type === 'order-popup' ? activePopup.position : 'long';
  const tpslKey = initialPos === 'hedge' ? 'hedge' : initialPos === 'short' ? 'short' : 'long';
  const [tpslRatio, setTpslRatio] = useState<number>(globalShapeStore.getState().lastTPSLByDirection[tpslKey]);
  const [tpslInputValue, setTpslInputValue] = useState<number | null>(null);
  const [tpslDropdownOpen, setTpslDropdownOpen] = useState(false);
  const [priceMode, setPriceMode] = useState<PriceMode>('instant');
  // const [limitPrice, setLimitPrice] = useState<number | null>(null); // TODO: Limit Price 미구현
  const [priceModeOpen, setPriceModeOpen] = useState(false);

  if (!activePopup || activePopup?.type !== 'order-popup') return null;

  const { chartId, position } = activePopup;
  if (position === 'hedge' && !hedgeMode) return null;

  // TP/SL 비율 변경 핸들러
  const handleTpslChange = (v: number) => {
    if (v > 100) return;
    if (v < 1) {
      notification.warning({
        message: 'TP/SL 비율 오류',
        description: '비율은 1 이상이어야 합니다.',
        placement: 'topRight',
        duration: 3,
      });
      return;
    }
    setTpslRatio(v);

    // 다음 팝업 오픈 시 마지막으로 쓴 비율을 복원하기 위해 전역 스토어에 영속 저장
    const dir = position === 'hedge' ? 'hedge' : position === 'short' ? 'short' : 'long';
    globalShapeStore.getState().updateLastTPSLByDirection(dir, v);
  };

  const buildNode = (positionType: PositionType, price: number) => {
    const shapeUuid = crypto.randomUUID();
    const now = Date.now();
    const baseStyle = {
      ...ORDER_LINE_RENDER_DEFAULTS,
      yValue: price,
      uuid: shapeUuid,
      type: ShapeType.ORDER_LINE,
      draggable: true,
      positionType,
      amount: inputAmount ?? 0,
      selected: false,
      tpslPrice: [{ tp: 0, sl: 0 }],
      chartId,
      createdAt: now,
      updatedAt: now,
    };

    // 기본값(보라색)을 롱/숏에 맞는 색으로 덮어쓰기 위해 미리 계산
    const color = positionType === 'long' ? COLORS.long : COLORS.short;
    return {
      ...baseStyle,
      stroke: color,
      textFill: color,
      edge: { ...baseStyle.edge, stroke: color },
    };
  };

  const onConfirmOrder = () => {
    if (!yCoordinateValue) return;
    if (!chartId) return;
    if (!inputAmount || inputAmount <= 0) {
      notification.warning({
        message: 'Amount 오류',
        description: 'Amount는 0보다 커야 합니다.',
        placement: 'topRight',
        duration: 3,
      });
      return;
    }

    const price = yCoordinateValue;

    // hedge 확정 시 long/short/hedge 모두 같은 비율로 동기화 → hedgeRatioEqual 조건 충족
    if (position === 'hedge') {
      globalShapeStore.getState().updateLastTPSLByDirection('long', tpslRatio);
      globalShapeStore.getState().updateLastTPSLByDirection('short', tpslRatio);
      globalShapeStore.getState().updateLastTPSLByDirection('hedge', tpslRatio);
    }

    setRenderNodes((prev: RenderNodesMap) => {
      const existing = prev[chartId]?.[ShapeType.ORDER_LINE] ?? [];
      // hedge 진입 시 long + short 오더라인을 동시에 생성
      if (position === 'hedge') {
        return {
          ...prev,
          [chartId]: {
            ...prev[chartId],
            [ShapeType.ORDER_LINE]: [...existing, buildNode('long', price), buildNode('short', price)],
          },
        };
      }
      return {
        ...prev,
        [chartId]: {
          ...prev[chartId],
          // hedge는 위 if 블록에서 처리됨 → TypeScript narrowing으로 position이 PositionType으로 확정
          [ShapeType.ORDER_LINE]: [...existing, buildNode(position, price)],
        },
      };
    });

    setShapeTypeToDraw(ShapeType.ORDER_LINE);
    setActivePopup(null);
  };

  // ─── 버튼 색상·텍스트 ────────────────────────────────────────────────
  const { color: btnColor, label: btnLabel } = getPositionStyle(position);

  // ─── TP/SL 드롭다운 메뉴 ────────────────────────────────────────────
  const tpslMenu = buildTpslMenu({
    currentRatio: tpslRatio,
    color: btnColor,
    inputValue: tpslInputValue,
    onInputChange: setTpslInputValue,
    onSelect: handleTpslChange,
    onClose: () => setTpslDropdownOpen(false),
    width: 134,
  });

  // ─── Instant / Limit Price 드롭다운 메뉴 ────────────────────────────
  const priceModeItems: MenuProps['items'] = [
    {
      key: 'instant',
      label: (
        <div
          onClick={e => {
            // Limit Price 구현 시 드롭다운이 열린 채로 입력칸을 보여줘야 하므로 직접 제어 방식 사용
            // Instant Price에서는 stopPropagation 없이도 동작하지만 일관성을 위해 유지
            e.stopPropagation();
            setPriceMode('instant');
            setPriceModeOpen(false);
          }}
        >
          Instant Price
        </div>
      ),
    },
    // TODO: Limit Price 미구현 — 구현 시 주석 해제
    // {
    //   key: 'limit',
    //   label: (
    //     <div>
    //       <div onClick={e => { e.stopPropagation(); setPriceMode('limit'); }}>
    //         Limit Price
    //       </div>
    //       {priceMode === 'limit' && (
    //         <>
    //           <InputNumber
    //             value={limitPrice}
    //             onChange={v => setLimitPrice(v === 0 ? null : v)}
    //             placeholder="가격 입력"
    //             min={0}
    //             size="small"
    //             controls={false}
    //             style={{ width: '100%', marginTop: 4 }}
    //             formatter={v => (v != null ? Number(v).toLocaleString() : '')}
    //             parser={v => (v ? parseFloat(v.replace(/,/g, '')) : 0)}
    //             onClick={e => e.stopPropagation()}
    //           />
    //           <button
    //             type="button"
    //             style={{ width: '100%', marginTop: 4, backgroundColor: btnColor, color: 'white', border: 'none', borderRadius: 4, padding: '4px 0', fontWeight: 'bold', cursor: 'pointer', fontSize: 12 }}
    //             onClick={e => { e.stopPropagation(); setPriceModeOpen(false); }}
    //           >
    //             Enter
    //           </button>
    //         </>
    //       )}
    //     </div>
    //   ),
    // },
  ];

  const priceModeMenu: MenuProps = { items: priceModeItems, selectedKeys: [priceMode] };

  return (
    <div
      ref={ref}
      className="absolute z-1000 flex flex-row w-[300px] rounded-[4px] shadow-sm overflow-hidden"
      style={{ top: activePopup.coords.y, left: activePopup.coords.x - 80 }}
    >
      {/* ── 왼쪽 패널: Amount + TP/SL ── */}
      <div className="flex flex-col flex-1 p-2 gap-2" style={{ backgroundColor: token.colorBgContainer }}>
        {/* Input Amount */}
        <InputNumber
          value={inputAmount}
          onChange={v => setInputAmount(v)}
          placeholder="Input Amount"
          min={0}
          style={{
            width: '100%',
            height: 24, // size="small" Button(24px)과 높이를 맞추기 위해 명시적으로 지정
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 400,
          }}
          styles={{ input: { paddingInline: 2, textAlign: 'right', paddingRight: 8 } }}
          formatter={v => (v != null ? Number(v).toLocaleString() : '')}
          parser={v => (v ? parseFloat(v.replace(/,/g, '')) : 0)}
          onPressEnter={onConfirmOrder}
          suffix={<span style={{ color: COLORS.placeholder, fontSize: 12 }}>USDT</span>}
        />

        {/* TP/SL 드롭다운 */}
        <Dropdown
          menu={tpslMenu}
          trigger={['hover']}
          open={tpslDropdownOpen}
          onOpenChange={open => {
            setTpslDropdownOpen(open);
            if (!open) setTpslInputValue(null);
          }}
        >
          <Button
            size="small"
            style={{
              width: '100%',
              borderRadius: 4,
              textAlign: 'left',
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            <span style={{ position: 'relative', display: 'block', width: '100%' }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {tpslRatio !== undefined ? tpslRatio.toFixed(2) : 'TP/SL'}
              </span>
              <CaretDownOutlined style={{ fontSize: 10, float: 'right' }} />
            </span>
          </Button>
        </Dropdown>
      </div>

      {/* ── 오른쪽 패널: Price + Enter 버튼 ── */}
      <div
        className="flex flex-col items-stretch flex-1 p-2 gap-2 rounded-r-[4px]"
        style={{ backgroundColor: COLORS.panel }}
      >
        <Dropdown
          menu={{ ...priceModeMenu, style: { width: 134 } }}
          trigger={['click']}
          open={priceModeOpen}
          onOpenChange={setPriceModeOpen}
        >
          <Button
            size="small"
            style={{
              width: '100%',
              borderColor: 'white',
              borderRadius: 4,
              backgroundColor: 'transparent',
              color: 'white',
            }}
          >
            <span style={{ position: 'relative', display: 'block', width: '100%' }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {priceMode === 'instant' ? 'Instant Price' : 'Limit Price'}
              </span>
              <CaretDownOutlined style={{ fontSize: 10, float: 'right' }} />
            </span>
          </Button>
        </Dropdown>

        {/* Enter 버튼 — Instant면 현재가 인라인, Limit면 입력가 표시 */}
        <Button
          size="small"
          style={{
            backgroundColor: btnColor,
            borderColor: 'white',
            borderRadius: 4,
            color: 'white',
            fontWeight: 600,
          }}
          onClick={onConfirmOrder}
        >
          {btnLabel}
        </Button>
      </div>
    </div>
  );
});

PlaceOrderPopup.displayName = 'PlaceOrderPopup';
